import { useEffect, useMemo } from 'react';
import Calendar, { calendarHooks } from '@screentone/addon-calendar';
import { Button, Dialog, Group, Loader } from '@screentone/core';
import { zonedTimeToUtc } from 'date-fns-tz';

import { PublicationSetting } from 'data/generated/graphql';
import { getNextQuarterHour, getPaddedTime } from 'utils/newsletters';
import styles from './ScheduleDialog.module.scss';

interface ScheduleDialogProps {
  dialogTitle: string;
  onSchedule: (publishUtc: number) => Promise<void> | void;
  onCancel: () => void;
  afterCalendarContent?: React.ReactNode;
  initialTime?: string;
  initialDate?: Date;
  minDate?: Date;
  isLoading?: boolean;
  publicationSetting?: PublicationSetting;
  latestEmbargoTimestamp?: number;
  hasEmbargo?: boolean;
}

export const ScheduleDialog = ({
  dialogTitle,
  initialTime: propInitialTime,
  initialDate: propInitialDate,
  onSchedule,
  onCancel,
  afterCalendarContent,
  minDate: propMinDate,
  isLoading = false,
  publicationSetting,
  latestEmbargoTimestamp,
  hasEmbargo = false
}: ScheduleDialogProps) => {
  // Calculate initial values if not provided explicitly
  const { computedInitialDate, computedInitialTime, computedMinDate } = useMemo(() => {
    // If explicit values are provided, use them
    if (propInitialTime && propInitialDate && propMinDate) {
      return {
        computedInitialDate: propInitialDate,
        computedInitialTime: propInitialTime,
        computedMinDate: propMinDate
      };
    }

    // Calculate values based on publication settings and embargo
    const calculatedInitialDate = getNextQuarterHour(publicationSetting, latestEmbargoTimestamp ?? Date.now());
    let calculatedInitialTime = getPaddedTime(calculatedInitialDate);
    const calculatedMinDate = new Date(calculatedInitialDate.getTime() - 60000); // one minute before

    if (hasEmbargo && latestEmbargoTimestamp) {
      // Use the minute marker from the embargo time instead of the next available quarter hour
      // to prevent setting it too far in the future post-embargo
      const tempDate = new Date(latestEmbargoTimestamp);
      calculatedMinDate.setMinutes(tempDate.getMinutes() + 5); // min date is five minutes after embargo time
      calculatedInitialTime = getPaddedTime(calculatedMinDate);
    }

    return {
      computedInitialDate: calculatedInitialDate,
      computedInitialTime: calculatedInitialTime,
      computedMinDate: calculatedMinDate
    };
  }, [propInitialTime, propInitialDate, propMinDate, publicationSetting, latestEmbargoTimestamp, hasEmbargo]);

  // Use the current time plus one year as the max date, or minDate + one year if minDate is provided.
  // Prevents using maxDate < minDate on embargoed content
  const oneYearFromNow = computedMinDate.getTime() + 365 * 24 * 60 * 60 * 1000;

  const { onSelect, selectedDate, selectedTime } = calendarHooks.useDateAndTime();

  useEffect(() => {
    onSelect({ selectedTime: computedInitialTime, selectedDate: computedInitialDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimeSelect = (time: string) => {
    onSelect({ selectedTime: time });
  };

  const handleDateSelect = (date: Date) => {
    onSelect({ selectedDate: date });
  };

  const handleSchedule = async () => {
    if (selectedDate !== null) {
      let timestamp: number;

      if (publicationSetting) {
        // Extract date/time components from selectedDate (which is in browser's local timezone)
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const hour = String(selectedDate.getHours()).padStart(2, '0');
        const minute = String(selectedDate.getMinutes()).padStart(2, '0');

        // Create ISO string without timezone info
        const dateString = `${year}-${month}-${day}T${hour}:${minute}:00`;

        // Interpret this date/time as being in the publication's preferred timezone, then convert to UTC
        const utcDate = zonedTimeToUtc(dateString, publicationSetting.preferredTimezone.timezone);
        timestamp = utcDate.getTime();
      } else {
        // No publication setting, use the date as-is (local timezone interpretation)
        timestamp = selectedDate.getTime();
      }

      await onSchedule(timestamp);
    }
  };

  return (
    <Dialog status="open" className={styles.dialog} onDismiss={onCancel} focusTrap={false}>
      <Dialog.Title data-testid="schedule-dialog-title">{dialogTitle}</Dialog.Title>
      <Dialog.Content data-testid="schedule-dialog-content">
        <Group wrap={false} gap="none" valign="start" data-testid="schedule-dialog-calendar">
          <Calendar
            onSelect={handleDateSelect}
            selectedDate={selectedDate}
            placeholder="MM/DD/YYYY"
            showCalendarOnly
            minDate={computedMinDate}
            maxDate={new Date(oneYearFromNow)}
          >
            <Calendar.TimeInput onSelect={handleTimeSelect} selectedTime={selectedTime} timeInterval={15} />
          </Calendar>
        </Group>
        <br />
        {afterCalendarContent}
      </Dialog.Content>
      <Dialog.Actions>
        <Button tertiary onClick={onCancel} data-testid="schedule-dialog-cancel-button" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSchedule}
          disabled={!(selectedDate && selectedTime) || isLoading}
          data-testid="schedule-dialog-schedule-button"
        >
          {isLoading && <Loader size="md" margin={{ right: 'md' }} />}
          Schedule
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
